import { globalResizeObserver } from 'base/ui/measure';
import { NestedTagNode } from 'base/webflow/schema';
import { ElementInstance } from 'blocks/editor/model';
import { action, observable, reaction } from 'mobx';
import { computedFn } from 'mobx-utils';
import { BoundsMeasurer } from './bounds';
import { COMPONENT_RENDERER_OUTER_DATASET_KEY } from './component_renderer';
import { AddEffect } from './decorators';

const ROOT_NODE_KEY = 'root';
const DROP_PREVIEW_ELEMENT_KEY = 'drop-preview';

export type NodeMeasurements = Readonly<{
  node: NestedTagNode;
  // scaled box, to position the overlay text box
  box: DOMRect;
  contentScale: number;
  style: CSSStyleDeclaration;
}>;

/**
 * Usage note: 1 instance should be made per "section" or other editing unit, since
 *   it measures everything together for efficiency.
 **/
export class NodeMeasurerStore {
  private readonly toMeasure = new Map<
    string,
    { node: NestedTagNode; domEl: HTMLElement }
  >();
  private readonly measurements = observable.map<string, NodeMeasurements>([], {
    deep: false,
    name: 'NodeMeasurerStore.measurements',
  });

  readonly dispose: () => void;

  constructor(bounds: BoundsMeasurer) {
    // If the component resizes (e.g. breakpoint changing), things have probably moved, but they
    // might not have resized (e.g. they are centered) so we add this trigger.
    this.dispose = reaction(() => bounds.bounds, this.delayedRefreshAll);
  }

  get(
    modelElement: ElementInstance | 'drop-preview',
    nodeId: string,
  ): NodeMeasurements | undefined {
    const key =
      modelElement === 'drop-preview'
        ? DROP_PREVIEW_ELEMENT_KEY
        : `${modelElement.oid}:${nodeId}`;
    const value = this.measurements.get(key);
    return value;
  }

  getRootNode(modelElement: ElementInstance | 'drop-preview') {
    return this.get(modelElement, ROOT_NODE_KEY);
  }

  /**
   * If 1 element inside a component resizes, the other elements are probably affected or moved.
   * So we always refresh the component as a whole.
   **/
  readonly refreshAll = action(() => {
    for (const [key, { node, domEl: originalDomEl }] of this.toMeasure) {
      this.refreshOnly(key, node, originalDomEl);
    }
  });

  private refreshOnly(key: string, node: NestedTagNode, domEl: HTMLElement) {
    const mm = measureRelativeToComponentRoot(domEl, node);
    const previous = this.measurements.get(key);
    if (previous && mm) {
      if (
        Math.abs(previous.box.width - mm.box.width) < EPSILON_PX &&
        Math.abs(previous.box.height - mm.box.height) < EPSILON_PX &&
        Math.abs(previous.box.x - mm.box.x) < EPSILON_PX &&
        Math.abs(previous.box.y - mm.box.y) < EPSILON_PX &&
        previous.contentScale === mm.contentScale &&
        // We assume the style doesn't change much.
        // However, when the element is hidden sometimes the style is totally blank
        // so we check a property that's never blank by default (font-family has
        // a default value)
        previous.style.fontFamily === mm.style.fontFamily
      ) {
        return;
      }
    }
    if (mm) {
      this.measurements.set(key, mm);
    } else {
      this.measurements.delete(key);
    }
  }

  private hasTimeout = false;
  private readonly delayedRefreshAll = () => {
    if (this.hasTimeout) return;
    this.hasTimeout = true;
    setTimeout(() => {
      this.hasTimeout = false;
      this.refreshAll();
    }, 0);
  };

  private readonly _measureEffect =
    (
      modelElement: ElementInstance | 'drop-preview',
      rootNode: boolean,
    ): AddEffect =>
    (node, ctx, domEl) => {
      const key =
        modelElement === 'drop-preview'
          ? DROP_PREVIEW_ELEMENT_KEY
          : `${modelElement.oid}:${rootNode ? ROOT_NODE_KEY : node._id}`;
      this.toMeasure.set(key, { node, domEl });
      this.refreshOnly(key, node, domEl);

      const dispose = globalResizeObserver.observe(domEl, this.refreshAll);
      return () => {
        dispose();
        this.toMeasure.delete(key);
        // Because the TagNodeView groups all effects together, if any other effect
        // changes this is re-run.  Deleting and then re-measuring will cause the
        // overlay to re-render, which is better avoided.  Hence we do not:
        //   this.measurements.delete(key);
      };
    };

  readonly measureEffect = computedFn(
    (modelElement: ElementInstance | 'drop-preview'): AddEffect =>
      this._measureEffect(modelElement, false),
  );

  readonly measureEffectForRootNode = computedFn(
    (modelElement: ElementInstance | 'drop-preview'): AddEffect =>
      this._measureEffect(modelElement, true),
  );
}

const EPSILON_PX = 0.01;

/* These tags are not included in the box model by definition. */
const HEAD_TAG_NAMES = ['STYLE', 'LINK', 'SCRIPT'];

/**
 * Returns the bounding box of the given element, in the component root's coordinate system.
 *
 * I.e. you could use this to position an overlay inside (`inset: a b c d`) the component root.
 **/
function measureRelativeToComponentRoot(
  domEl: HTMLElement,
  node: NestedTagNode,
) {
  const root = domEl.closest(`[${COMPONENT_RENDERER_OUTER_DATASET_KEY}]`);
  if (!root) return;
  const rootScale = parseFloat(
    root.getAttribute(COMPONENT_RENDERER_OUTER_DATASET_KEY)!,
  );
  let elStyle = window.getComputedStyle(domEl);
  if (elStyle.display === 'contents') {
    // A display contents element has no box of its own, so we measure its child.
    const children = [...domEl.children].filter(
      (e) => !HEAD_TAG_NAMES.includes(e.tagName),
    );
    if (children.length > 1) {
      // webflow doesn't let you create a `display: contents` element.  It is created by our
      // "mechanism=replace-self" slots.
      throw new Error(
        'display: contents element has more than 1 child - can not be measured',
      );
    }
    const firstChild = children[0];
    if (firstChild instanceof HTMLElement) {
      domEl = firstChild;
      elStyle = window.getComputedStyle(domEl);
    }
  }

  const rootBox = root.getBoundingClientRect();
  const elBox = domEl.getBoundingClientRect();
  if (elBox.width === 0 && elBox.height === 0) {
    return;
  }

  // We prefer computedStyle width since it is accurate.
  // computedStyle doesn't work for inline elements, so we use offsetWidth.  It may be
  // an underestimate (due to rounding) so we add 1 to prevent text wrapping.
  const computedStyleWorks = elStyle.width !== 'auto';
  const cssWidth = computedStyleWorks
    ? parseFloat(elStyle.width)
    : domEl.offsetWidth;
  const cssHeight = computedStyleWorks
    ? parseFloat(elStyle.height)
    : domEl.offsetHeight;
  const fudgeFactor = computedStyleWorks ? 0 : 0.5;
  const sx = rootScale * (elBox.width ? cssWidth / elBox.width : 1);
  const sy = rootScale * (elBox.height ? cssHeight / elBox.height : 1);
  const adjustedBox = new DOMRect(
    (elBox.x - rootBox.x - fudgeFactor) * sx,
    (elBox.y - rootBox.y - fudgeFactor) * sy,
    (elBox.width + 2 * fudgeFactor) * sx,
    (elBox.height + 2 * fudgeFactor) * sy,
  );

  return {
    box: adjustedBox,
    contentScale: rootScale,
    style: elStyle,
    node,
  };
}

/**
 * Converts between client (mouse) coordinates and the component root's coordinate system.
 **/
export function clientPointToComponentRoot(
  domEl: HTMLElement,
  clientX: number,
  clientY: number,
) {
  const root = domEl.closest(`[${COMPONENT_RENDERER_OUTER_DATASET_KEY}]`);
  if (!root) throw new Error('No component root found');
  const rootBox = root.getBoundingClientRect();
  const elStyle = window.getComputedStyle(domEl);
  const sx = parseFloat(elStyle.width) / rootBox.width;
  const sy = parseFloat(elStyle.height) / rootBox.height;
  return {
    x: (clientX - rootBox.x) * sx,
    y: (clientY - rootBox.y) * sy,
  };
}
